// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getProject } from '@/project/api/get-project';
import { useQuery } from '@tanstack/react-query';

interface UseProjectOptions {
  /**
   * The key of the project to fetch.
   */
  key: string | null | undefined;

  /**
   * Whether the query should be enabled.
   * If false, the query will not execute.
   */
  enabled?: boolean;
}

/**
 * A React hook that requests a single project from the remote API with no caching.
 * The hook will refetch whenever the project key changes.
 *
 * @param options.key - The key of the project to fetch.
 * @param options.enabled - Whether the query should be enabled. Defaults to true when key is provided.
 */
export function useProject(options: UseProjectOptions) {
  const isEnabled = options.enabled ?? !!options.key;

  const { data, isLoading, isFetching, error, refetch, isPending } = useQuery({
    queryKey: ['project', options.key],
    queryFn: async () => {
      if (!options.key) {
        throw new Error('Project key is required');
      }
      return getProject(options.key);
    },
    enabled: isEnabled,
    staleTime: 0, // Always consider data stale - no caching
    gcTime: 0, // Don't keep in cache - immediate garbage collection
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  return {
    project: data?.project ?? null,
    metadata: data?.metadata ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
    isPending,
  };
}
